import { useState } from "react";
import { Cloud, RefreshCw, Plug } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  useCspmSummary,
  useCspmFindings,
  useCspmScan,
  useSuppressFinding,
  useUnsuppressFinding,
} from "@/hooks/use-api";
import { PostureScore } from "@/components/cspm/posture-score";
import { FindingsTable } from "@/components/cspm/findings-table";
import { APP_ROUTES, appRouteForPathname } from "@/lib/app-routes";

const STATUS_TABS = ["open", "resolved", "suppressed"] as const;
const PROVIDER_TABS = [
  { value: undefined, label: "All" },
  { value: "aws", label: "AWS" },
  { value: "azure", label: "Azure" },
  { value: "gcp", label: "GCP" },
] as const;

export default function CloudPosturePage() {
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [providerFilter, setProviderFilter] = useState<string | undefined>();

  const summary = useCspmSummary();
  const findings = useCspmFindings({
    status: statusFilter,
    cloud_provider: providerFilter,
    limit: 50,
  });
  const scan = useCspmScan();
  const suppress = useSuppressFinding();
  const unsuppress = useUnsuppressFinding();

  const score = summary.data?.score ?? 100;
  const bySeverity = summary.data?.by_severity;
  const showContent =
    summary.isLoading ||
    summary.isError ||
    (summary.data?.total ?? 0) > 0 ||
    Object.keys(summary.data?.by_provider ?? {}).length > 0;

  return (
    <div className="space-y-6">
      {/* Header with scan button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cloud className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Cloud Posture</h1>
            <p className="text-sm text-muted-foreground">
              Security configuration across your cloud environments
            </p>
          </div>
        </div>
        <Button
          onClick={() => scan.mutate()}
          disabled={scan.isPending}
          variant="outline"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${scan.isPending ? "animate-spin" : ""}`} />
          {scan.isPending ? "Scanning..." : "Run Scan"}
        </Button>
      </div>

      {summary.isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load cloud posture data. Data will retry automatically.
        </div>
      )}

      {/* Empty state when no cloud connectors are configured.
          Uses both total===0 and empty by_provider as the signal: a tenant with
          connectors that have findings will have total>0; one with connectors but
          no findings yet will appear in by_provider. */}
      {!summary.isLoading &&
        !summary.isError &&
        !!summary.data &&
        summary.data.total === 0 &&
        Object.keys(summary.data.by_provider).length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20 text-center">
          <div className="rounded-full bg-muted p-4">
            <Plug className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-base font-semibold">No cloud connectors yet</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Connect AWS, Azure, or GCP to see misconfigurations and posture scoring.
          </p>
          <Link
            to={appRouteForPathname(location.pathname, APP_ROUTES.integrations)}
            className={cn(buttonVariants({ variant: "outline" }), "mt-6")}
          >
            Add cloud connector
          </Link>
        </div>
      )}

      {/* Score + summary cards (hidden while empty state is shown) */}
      {showContent && (
        <div className="grid gap-4 md:grid-cols-5">
        <Card className="md:col-span-1">
          <CardContent className="flex items-center justify-center pt-6">
            {summary.isLoading ? (
              <div className="h-32 w-32 animate-pulse rounded-full bg-muted" />
            ) : (
              <PostureScore score={score} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {bySeverity?.critical ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">
              {bySeverity?.high ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Medium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {bySeverity?.medium ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Low</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">
              {bySeverity?.low ?? 0}
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Provider filter tabs */}
      {showContent && (
        <div className="flex items-center gap-4">
        <div className="flex gap-1 rounded-lg border bg-muted/50 p-1">
          {PROVIDER_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setProviderFilter(tab.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                providerFilter === tab.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 rounded-lg border bg-muted/50 p-1">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                statusFilter === s
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        </div>
      )}

      {/* Findings table */}
      {showContent && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Findings ({findings.data?.findings?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FindingsTable
              findings={findings.data?.findings ?? []}
              isLoading={findings.isLoading}
              onSuppress={(id) => suppress.mutate(id)}
              onUnsuppress={(id) => unsuppress.mutate(id)}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
